import { WorkspaceSegments } from "./workspaceSegments";
import {
  ECOM_BALCAO_SCANIA_FIXED_SLUGS,
  getEcomBalcaoScaniaFixedSlug,
} from "./ecomBalcaoScaniaContentSlugs";

/** Tipos disponíveis para todos os workspaces (e-commerce / institucional base). */
export const ECOM_CORE_CONTENT_TYPES = Object.freeze([
  { value: "como_comprar", label: "Como comprar", defaultSlug: "como-comprar" },
  { value: "pagamento", label: "Pagamento", defaultSlug: "pagamento" },
  { value: "entregas", label: "Entregas & prazos", defaultSlug: "entregas" },
  { value: "trocas", label: "Trocas e devoluções", defaultSlug: "trocas" },
  { value: "faq", label: "Perguntas frequentes", defaultSlug: "faq" },
  {
    value: "privacidade",
    label: "Declaração de Privacidade",
    defaultSlug: "declaracao-de-privacidade",
  },
  { value: "cookies", label: "Política de Cookies", defaultSlug: "cookies" },
  { value: "termos", label: "Termos de uso", defaultSlug: "terms" },
  { value: "lgpd", label: "LGPD", defaultSlug: "lgpd" },
  { value: "links_rodape", label: "Links do rodapé", defaultSlug: "institucional" },
  { value: "sugestoes_busca", label: "Sugestões de busca", defaultSlug: "sugestoes-busca" },
  { value: "segmentos", label: "Segmentos", defaultSlug: "segmentos" },
  { value: "sobre", label: "Quem somos", defaultSlug: "sobre" },
  { value: "home_seo", label: "Texto SEO da home", defaultSlug: "home-seo" },
  {
    value: "pdp_retire_concessionaria",
    label: "PDP — Retire na Concessionária",
    defaultSlug: "retire-concessionaria",
  },
  {
    value: "pdp_aplicacao",
    label: "PDP — Aplicação",
    defaultSlug: "aplicacao",
  },
  {
    value: "pdp_whatsapp_frete",
    label: "PDP — Botão WhatsApp / frete",
    defaultSlug: "whatsapp-frete",
  },
  {
    value: "treinamento_plataforma",
    label: "Treinamento da plataforma",
    defaultSlug: "treinamento",
  },
  {
    value: "instagram_feed",
    label: "Últimos posts (Instagram)",
    defaultSlug: "instagram",
  },
]);

/**
 * Tipos extras para segmento transporte rodoviário (passagens / site de viagem).
 * Não aparecem para lojas ou outros segmentos.
 */
export const ECOM_PASSENGER_TRANSPORT_CONTENT_TYPES = Object.freeze([
  { value: "site_home", label: "Home do site", defaultSlug: "principal" },
  { value: "beneficios", label: "Benefícios e gratuidades", defaultSlug: "principal" },
  { value: "informacoes", label: "Informações para viagem", defaultSlug: "principal" },
  { value: "formas_pagamento", label: "Formas de pagamento", defaultSlug: "principal" },
  { value: "encomendas", label: "Encomendas", defaultSlug: "principal" },
  { value: "sustentabilidade", label: "Sustentabilidade", defaultSlug: "principal" },
  { value: "vale_transporte", label: "Vale transporte", defaultSlug: "principal" },
  { value: "trabalhe_conosco", label: "Trabalhe conosco", defaultSlug: "principal" },
  { value: "agencias", label: "Agências", defaultSlug: "principal" },
  { value: "contato", label: "Página de contato", defaultSlug: "principal" },
  { value: "elas", label: "Botão ELAS", defaultSlug: "principal" },
  { value: "rodape", label: "Rodapé do site", defaultSlug: "principal" },
  { value: "site_nav", label: "Menu do site", defaultSlug: "principal" },
]);

/**
 * Páginas do site de passagens com layout rico (pagina_institucional.v1).
 */
export const ECOM_PASSENGER_INSTITUTIONAL_PAGE_TYPES = new Set([
  "sobre",
  "beneficios",
  "informacoes",
  "sustentabilidade",
  "vale_transporte",
  "trabalhe_conosco",
  "formas_pagamento",
  "encomendas",
  "como_comprar",
  "elas",
]);

/** Tipos com editor JSON dedicado (não pagina_institucional). */
export const ECOM_PASSENGER_STRUCTURED_PAGE_TYPES = new Set([
  "agencias",
  "contato",
  "rodape",
  "site_home",
  "site_nav",
]);

/**
 * Páginas simples legado — vazio após migração institucional.
 * @deprecated
 */
export const ECOM_PASSENGER_SIMPLE_PAGE_TYPES = new Set([]);

/** @deprecated use ECOM_PASSENGER_SIMPLE_PAGE_TYPES */
export const ECOM_PASSENGER_RICH_LAYOUT_TYPES = ECOM_PASSENGER_SIMPLE_PAGE_TYPES;

/** Slugs fixos adicionais (rodoviário) — além dos do Balcão Scania. */
export const ECOM_PASSENGER_TRANSPORT_FIXED_SLUGS = Object.freeze({
  site_home: "principal",
  sobre: "principal",
  beneficios: "principal",
  informacoes: "principal",
  formas_pagamento: "principal",
  encomendas: "principal",
  sustentabilidade: "principal",
  vale_transporte: "principal",
  trabalhe_conosco: "principal",
  como_comprar: "principal",
  agencias: "principal",
  contato: "principal",
  elas: "principal",
  rodape: "principal",
  site_nav: "principal",
});

/** @deprecated legado Guerino — normalizado para site_home na UI e na API. */
export const LEGACY_SITE_HOME_TIPO = "guerino_home";

export const ECOM_CONTENT_TYPE_LABELS = Object.freeze(
  [...ECOM_CORE_CONTENT_TYPES, ...ECOM_PASSENGER_TRANSPORT_CONTENT_TYPES].reduce(
    (acc, t) => {
      acc[t.value] = t.label;
      return acc;
    },
    {
      [LEGACY_SITE_HOME_TIPO]: "Home do site (legado)",
    }
  )
);

export function normalizeEcomContentTipo(tipo) {
  const t = String(tipo || "").trim().toLowerCase();
  if (t === LEGACY_SITE_HOME_TIPO) return "site_home";
  return t;
}

/** @param {string} segment */
export function getEcomContentTypesForSegment(segment) {
  const core = [...ECOM_CORE_CONTENT_TYPES];
  if (segment === WorkspaceSegments.PassengerTransport) {
    return [...core, ...ECOM_PASSENGER_TRANSPORT_CONTENT_TYPES];
  }
  return core;
}

/** Slug fixo por tipo — no segmento rodoviário, slugs do site de passagens têm prioridade. */
export function getEcomContentFixedSlug(tipo, segment) {
  const key = normalizeEcomContentTipo(tipo);
  if (segment === WorkspaceSegments.PassengerTransport) {
    const passenger = ECOM_PASSENGER_TRANSPORT_FIXED_SLUGS[key];
    if (passenger) return passenger;
  }
  return getEcomBalcaoScaniaFixedSlug(key) ?? null;
}

export function getEcomContentTypeLabel(tipo) {
  const key = normalizeEcomContentTipo(tipo);
  return ECOM_CONTENT_TYPE_LABELS[key] || ECOM_CONTENT_TYPE_LABELS[tipo] || tipo || "—";
}

export const ECOM_LONG_HTML_CONTENT_TYPES = new Set([
  "como_comprar",
  "pagamento",
  "entregas",
  "trocas",
  "privacidade",
  "cookies",
  "home_seo",
  "pdp_retire_concessionaria",
  "pdp_aplicacao",
]);

export const ECOM_ALL_CONTENT_TYPE_VALUES = new Set([
  ...ECOM_CORE_CONTENT_TYPES.map((t) => t.value),
  ...ECOM_PASSENGER_TRANSPORT_CONTENT_TYPES.map((t) => t.value),
  LEGACY_SITE_HOME_TIPO,
]);
